import React from 'react';

import SectionModel from '../../models/sectionModel';
import {
    COURIER_DELIVERY_FEES_SETTING,
    COURIER_DELIVERY_FEES_SETTING_GET,
    COURIER_DELIVERY_FEES_TEMPLATE_SETTING,
    COURIER_DELIVERY_FEES_TEMPLATE_SETTING_GET,
} from '../../store/reducers/restaurant/restaurant-actions';

import {
    CONTROL_TYPE, getCurrencyIcons,
    getMerchantDeliveryCostCalculationModeItems,
    MODEL_CONTROL_TYPE,
    MODEL_TYPE,
    PRICE_CALCULATION_MODE, TYPE_ACTION,
    TYPE_SECTION_MODEL,
} from '../../utils/enums';
import { getInitModel } from '../initModels';

import RegionDeliveryPrice from '../../app/components/RegionDeliveryPrice';
import { isEmptyMode } from '../../utils/objects-util';
import { getRegions, isEndlessCycleMode, isMobileWidth } from '../restaurant';
import { createControlModel } from '../controlModelFactory';
import { createBreadcrumbModel } from '../createBreadcrumbModel';
import FilterOptionsModel from "../../models/filterOptionsModel.js";
import i18n from "i18next";
import { sortItemsByProp } from "../filter.js";

export function createCourierDeliveryFeesSectionModel({ ...args }) {
    return new SectionModel({
        columns: [
            {
                title: 'settings.map.COURIER_PRICE_CALCULATION_MODE',
                accessor: 'calculationMode',
                isEditable: true,
                connect: () => ({
                    values: getMerchantDeliveryCostCalculationModeItems(),
                }),
                type: CONTROL_TYPE.select,
            },
            {
                title: 'settings.map.GENERAL_DELIVERY_PRICE',
                accessor: 'maxDeliveryPrice',
                isEditable: true,
                inlineType: 'inlineOdd',
                isShow: value =>
                    !isEmptyMode(value.calculationMode) &&
                    value.calculationMode === PRICE_CALCULATION_MODE.fixedRegionBased,
                type: CONTROL_TYPE.number,
            },
            {
                title: 'settings.personalSettings.shipmentPriceSettings.LABEL',
                isEditable: true,
                type: CONTROL_TYPE.sectionGroup,
                items: [
                    {
                        title: 'settings.personalSettings.shipmentPriceSettings.BASIS_PRICE',
                        accessor: 'basisPrice',
                        isEditable: true,
                        inlineType: 'inlineOdd',
                        type: CONTROL_TYPE.number,
                    },
                    {
                        title: 'settings.personalSettings.shipmentPriceSettings.BASIS_DISTANCE_IN_METERS',
                        accessor: 'basisDistanceInMeters',
                        isEditable: true,
                        inlineType: 'inlineEven',
                        type: CONTROL_TYPE.number,
                    },
                    {
                        title: 'settings.personalSettings.shipmentPriceSettings.PRICE_PER_KM',
                        accessor: 'pricePerKm',
                        isEditable: true,
                        inlineType: 'inlineOdd',
                        type: CONTROL_TYPE.number,
                    },
                    {
                        title: 'settings.personalSettings.shipmentPriceSettings.MAX_DELIVERY_PRICE',
                        accessor: 'maxDeliveryPrice',
                        isEditable: true,
                        inlineType: 'inlineEven',
                        type: CONTROL_TYPE.number,
                    },
                ],
                isShow: value =>
                    !isEmptyMode(value.calculationMode) &&
                    value.calculationMode !== PRICE_CALCULATION_MODE.fixedRegionBased,
            },
            {
                title: 'settings.personalSettings.shipmentPriceSettings.PRICE_CHANGE_GROUP_LABEL',
                isEditable: true,
                type: CONTROL_TYPE.sectionGroup,
                items: [
                    {
                        title: 'settings.personalSettings.shipmentPriceSettings.MIN_ORDER_COUNT_TO_APPLY_PRICE_CHANGE',
                        accessor: 'minOrderCountToApplyPriceChange',
                        isEditable: true,
                        type: CONTROL_TYPE.number,
                    },
                    {
                        title: 'settings.personalSettings.shipmentPriceSettings.ORDER_COUNT_PRICE_CHANGE_INCREMENT',
                        accessor: 'orderCountPriceChangeIncrement',
                        isEditable: true,
                        type: CONTROL_TYPE.number,
                        measure: '%',
                        isShow: item => !item.useAbsValueForOrderCountPriceChange,
                    },
                    {
                        title: 'settings.personalSettings.shipmentPriceSettings.ORDER_COUNT_PRICE_CHANGE_INCREMENT',
                        accessor: 'orderCountPriceChangeIncrement',
                        isEditable: true,
                        type: CONTROL_TYPE.number,
                        measure: getCurrencyIcons('ils'),
                        isShow: item => item.useAbsValueForOrderCountPriceChange,
                    },
                    {
                        title: 'settings.personalSettings.shipmentPriceSettings.USE_ABS_VALUE_FOR_ORDER_COUNT_PRICE_CHANGE',
                        accessor: 'useAbsValueForOrderCountPriceChange',
                        isEditable: true,
                        isInline: true,
                        type: CONTROL_TYPE.switch,
                    },
                ],
                isShow: item => !isEndlessCycleMode()
            },
            {
                title: 'settings.map.DEPOT_DELIVERY_PRICE_PERREGION_HEADER_LABEL',
                isEditable: true,
                type: CONTROL_TYPE.sectionGroup,
                isShow: value =>
                    !isEmptyMode(value.calculationMode) && value.calculationMode !== PRICE_CALCULATION_MODE.distanceBased,
                items: [
                    {
                        accessor: 'regionConfigurations',
                        itemValue: {
                            key: 'id',
                            component: function (item, onChange, onDelete) {
                                return (
                                    <RegionDeliveryPrice
                                        item={item}
                                        handleOnChange={onChange}
                                        handleOnDeleteItem={onDelete}
                                    />
                                );
                            },
                        },
                        isEditable: true,
                        type: CONTROL_TYPE.listItem,
                    },
                    {
                        title: 'settings.users.DEPOT_USERS_ADD_BUTTON',
                        accessor: 'regionConfigurations',
                        values: function (item) {
                            return getRegions()
                                .map(region => ({ ...region, key: region.id, value: region.name }));
                        },
                        isEditable: true,
                        type: CONTROL_TYPE.selectRegionWithAdd,
                    },
                ],
            }, {
                title: 'settings.personalSettings.shipmentPriceSettings.OPERATION_SETTINGS_LABEL',
                isEditable: true,
                type: CONTROL_TYPE.sectionGroup,
                items: [
                    {
                        accessor: 'doNotAllowCycleReject',
                        isEditable: true,
                        isInline: true,
                        title: 'settings.personalSettings.shipmentPriceSettings.DO_NOT_ALLOWED_CYCLE_REJECT',
                        type: CONTROL_TYPE.switch,
                    },
                    {
                        accessor: 'excludeFromAutomation',
                        isEditable: true,
                        isInline: true,
                        title: 'settings.personalSettings.shipmentPriceSettings.EXCLUDE_FROM_AUTOMATION',
                        type: CONTROL_TYPE.switch,
                    },
                    {
                        accessor: 'isPriorityAssignment',
                        isEditable: true,
                        isInline: true,
                        title: 'settings.personalSettings.shipmentPriceSettings.IS_PRIORITY_ASSIGNMENT',
                        type: CONTROL_TYPE.switch,
                    }
                ],
            }
        ],
        actionName: COURIER_DELIVERY_FEES_SETTING,
        actionLoad: COURIER_DELIVERY_FEES_SETTING_GET,
        initNewItemModel: item => getInitModel(MODEL_TYPE.courierDeliveryFees, item),
        typeAction: args.typeAction,
        stayAfterSave: true,
        typeModel: TYPE_SECTION_MODEL.courierDeliveryFees,
        saveControlsConfig: {
            cancel: { title: 'basic.RESET_BUTTON', action: () => { }, isInit: true },
        },
        controls: [
            createControlModel({
                type: MODEL_CONTROL_TYPE.back,
                isShow: isMobileWidth,
            }),
        ],
        label: 'settings.courierDeliveryFeesSettings.LABEL',
        breadcrumbs: [
            createBreadcrumbModel({ label: 'header.menu.SETTINGS_LABEL' }),
            createBreadcrumbModel({ label: 'settings.COURIER_MANAGEMENT_LABEL' }),
        ],
        mappingLambda: state => {
            return {
                id: 'courierDeliveryFees',
                ...state.restaurant.data.shipmentPriceSettings,
            };
        },
    });
}

export function createCourierDeliveryFeesTemplateSectionModel({ ...args }) {
    return new SectionModel({
        filterOptions: createCourierDeliveryFeesTemplateFilter(),
        columns: [
            {
                title: 'settings.courierDeliveryFeesSettings.NAME_LABEL',
                accessor: 'name',
            },
            {
                title: 'settings.courierDeliveryFeesSettings.ACTIONS_LABEL',
                width: '110px'
            },
            {
                title: '',
                hideInHeader: true,
                type: CONTROL_TYPE.link,
                link: TYPE_SECTION_MODEL.courierDeliveryFeesTemplateNew,
                iconName: 'copy',
                width: '40px',
                tooltipText: i18n.t('basic.COPY_BUTTON'),
            },
            {
                title: '',
                hideInHeader: true,
                type: CONTROL_TYPE.link,
                link: TYPE_SECTION_MODEL.courierDeliveryFeesTemplateEdit,
                iconName: 'editItem',
                width: '40px',
                tooltipText: i18n.t('basic.EDIT_BUTTON'),
            }
        ],
        actionName: COURIER_DELIVERY_FEES_TEMPLATE_SETTING,
        actionLoad: COURIER_DELIVERY_FEES_TEMPLATE_SETTING_GET,
        hideHeaderItemControls: true,
        hideCellForSort: true,
        isForceUpdate: true,
        initNewItemModel: item => item,
        typeModel: TYPE_SECTION_MODEL.courierDeliveryFeesTemplate,
        controls: [
            createControlModel({
                type: MODEL_CONTROL_TYPE.add,
                url: TYPE_SECTION_MODEL.courierDeliveryFeesTemplateNew,
            }),
        ],
        sortByFunc: (items) => sortItemsByProp(items),
        label: 'settings.courierDeliveryFeesSettings.TEMPLATE_LABEL',
        breadcrumbs: [
            createBreadcrumbModel({ label: 'header.menu.SETTINGS_LABEL' }),
            createBreadcrumbModel({ label: 'settings.COURIER_MANAGEMENT_LABEL' }),
        ],
        mappingLambda: state => {
            return state.restaurant.data.shipmentTemplateSettings;
        },
    })
}

export function createCourierDeliveryFeesTemplateEditSectionModel({ ...args }) {
    return new SectionModel({
        columns: [
            {
                title: 'settings.courierDeliveryFeesSettings.NAME_LABEL',
                accessor: 'name',
                isEditable: true,
                type: CONTROL_TYPE.text,
                isFuncRequired: item => !item?.name?.trim()?.length,
            },
            {
                title: 'settings.map.COURIER_PRICE_CALCULATION_MODE',
                accessor: 'calculationMode',
                isEditable: true,
                connect: () => ({
                    values: getMerchantDeliveryCostCalculationModeItems(),
                }),
                type: CONTROL_TYPE.select,
            },
            {
                title: 'settings.map.GENERAL_DELIVERY_PRICE',
                accessor: 'maxDeliveryPrice',
                isEditable: true,
                inlineType: 'inlineOdd',
                isShow: value =>
                    !isEmptyMode(value.calculationMode) &&
                    value.calculationMode === PRICE_CALCULATION_MODE.fixedRegionBased,
                type: CONTROL_TYPE.number,
            },
            {
                title: 'settings.personalSettings.shipmentPriceSettings.LABEL',
                isEditable: true,
                type: CONTROL_TYPE.sectionGroup,
                items: [
                    {
                        title: 'settings.personalSettings.shipmentPriceSettings.BASIS_PRICE',
                        accessor: 'basisPrice',
                        isEditable: true,
                        inlineType: 'inlineOdd',
                        type: CONTROL_TYPE.number,
                    },
                    {
                        title: 'settings.personalSettings.shipmentPriceSettings.BASIS_DISTANCE_IN_METERS',
                        accessor: 'basisDistanceInMeters',
                        isEditable: true,
                        inlineType: 'inlineEven',
                        type: CONTROL_TYPE.number,
                    },
                    {
                        title: 'settings.personalSettings.shipmentPriceSettings.PRICE_PER_KM',
                        accessor: 'pricePerKm',
                        isEditable: true,
                        inlineType: 'inlineOdd',
                        type: CONTROL_TYPE.number,
                    },
                    {
                        title: 'settings.personalSettings.shipmentPriceSettings.MAX_DELIVERY_PRICE',
                        accessor: 'maxDeliveryPrice',
                        isEditable: true,
                        inlineType: 'inlineEven',
                        type: CONTROL_TYPE.number,
                    },
                ],
                isShow: value =>
                    !isEmptyMode(value.calculationMode) &&
                    value.calculationMode !== PRICE_CALCULATION_MODE.fixedRegionBased,
            },
            {
                title: 'settings.personalSettings.shipmentPriceSettings.PRICE_CHANGE_GROUP_LABEL',
                isEditable: true,
                type: CONTROL_TYPE.sectionGroup,
                items: [
                    {
                        title: 'settings.personalSettings.shipmentPriceSettings.MIN_ORDER_COUNT_TO_APPLY_PRICE_CHANGE',
                        accessor: 'minOrderCountToApplyPriceChange',
                        isEditable: true,
                        type: CONTROL_TYPE.number,
                    },
                    {
                        title: 'settings.personalSettings.shipmentPriceSettings.ORDER_COUNT_PRICE_CHANGE_INCREMENT',
                        accessor: 'orderCountPriceChangeIncrement',
                        isEditable: true,
                        type: CONTROL_TYPE.number,
                        measure: '%',
                        isShow: item => !item.useAbsValueForOrderCountPriceChange,
                    },
                    {
                        title: 'settings.personalSettings.shipmentPriceSettings.ORDER_COUNT_PRICE_CHANGE_INCREMENT',
                        accessor: 'orderCountPriceChangeIncrement',
                        isEditable: true,
                        type: CONTROL_TYPE.number,
                        measure: getCurrencyIcons('ils'),
                        isShow: item => item.useAbsValueForOrderCountPriceChange,
                    },
                    {
                        title: 'settings.personalSettings.shipmentPriceSettings.USE_ABS_VALUE_FOR_ORDER_COUNT_PRICE_CHANGE',
                        accessor: 'useAbsValueForOrderCountPriceChange',
                        isEditable: true,
                        isInline: true,
                        type: CONTROL_TYPE.switch,
                    },
                ],
                isShow: item => !isEndlessCycleMode()
            },
            {
                title: 'settings.map.DEPOT_DELIVERY_PRICE_PERREGION_HEADER_LABEL',
                isEditable: true,
                type: CONTROL_TYPE.sectionGroup,
                isShow: value =>
                    !isEmptyMode(value.calculationMode) && value.calculationMode !== PRICE_CALCULATION_MODE.distanceBased,
                items: [
                    {
                        accessor: 'regionConfigurations',
                        itemValue: {
                            key: 'id',
                            component: function (item, onChange, onDelete) {
                                return (
                                    <RegionDeliveryPrice
                                        item={item}
                                        handleOnChange={onChange}
                                        handleOnDeleteItem={onDelete}
                                    />
                                );
                            },
                        },
                        isEditable: true,
                        type: CONTROL_TYPE.listItem,
                    },
                    {
                        title: 'settings.users.DEPOT_USERS_ADD_BUTTON',
                        accessor: 'regionConfigurations',
                        values: function (item) {
                            return getRegions()
                                .map(region => ({ ...region, key: region.id, value: region.name }));
                        },
                        isEditable: true,
                        type: CONTROL_TYPE.selectRegionWithAdd,
                    },
                ],
            }, {
                title: 'settings.personalSettings.shipmentPriceSettings.OPERATION_SETTINGS_LABEL',
                isEditable: true,
                type: CONTROL_TYPE.sectionGroup,
                items: [
                    {
                        accessor: 'doNotAllowCycleReject',
                        isEditable: true,
                        isInline: true,
                        title: 'settings.personalSettings.shipmentPriceSettings.DO_NOT_ALLOWED_CYCLE_REJECT',
                        type: CONTROL_TYPE.switch,
                    },
                    {
                        accessor: 'excludeFromAutomation',
                        isEditable: true,
                        isInline: true,
                        title: 'settings.personalSettings.shipmentPriceSettings.EXCLUDE_FROM_AUTOMATION',
                        type: CONTROL_TYPE.switch,
                    },
                    {
                        accessor: 'isPriorityAssignment',
                        isEditable: true,
                        isInline: true,
                        title: 'settings.personalSettings.shipmentPriceSettings.IS_PRIORITY_ASSIGNMENT',
                        type: CONTROL_TYPE.switch,
                    }
                ],
            }
        ],
        actionName: COURIER_DELIVERY_FEES_TEMPLATE_SETTING,
        actionLoad: COURIER_DELIVERY_FEES_TEMPLATE_SETTING_GET,
        parentUrl: TYPE_SECTION_MODEL.courierDeliveryFeesTemplate,
        initNewItemModel: item => getInitModel(MODEL_TYPE.courierDeliveryFees, item),
        typeAction: args.typeAction,
        canCopyItem: args.typeAction === TYPE_ACTION.new,
        resetAccessorsValueInCopy: args.typeAction === TYPE_ACTION.new ? ["name"] : null,
        typeModel: args.typeAction === TYPE_ACTION.edit ? TYPE_SECTION_MODEL.courierDeliveryFeesTemplateEdit : TYPE_SECTION_MODEL.courierDeliveryFeesTemplateNew,
        controls: [
            createControlModel({
                type: MODEL_CONTROL_TYPE.back,
            }),
        ],
        label: 'settings.courierDeliveryFeesSettings.TEMPLATE_LABEL',
        breadcrumbs: [
            createBreadcrumbModel({ label: 'header.menu.SETTINGS_LABEL' }),
            createBreadcrumbModel({ label: 'settings.COURIER_MANAGEMENT_LABEL' }),
        ],
        mappingLambda: state => {
            return state.restaurant.data.shipmentTemplateSettings;
        },
    });
}

function createCourierDeliveryFeesTemplateFilter() {
    let filterOptionsModel = new FilterOptionsModel();

    // filterOptionsModel.addOptions('search', CONTROL_TYPE.text, null, function(translations) {
    //     return translationsFilter(this.selected, translations);
    // });

    return filterOptionsModel;

}
